(() => {
  'use strict';

  const doc = document;
  const config = window.PSA || {};
  const routes = config.routes || {};
  const root = routes.root || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const liveRegion = () => doc.querySelector('[data-live-region]');
  const overlay = () => doc.querySelector('[data-page-overlay]');
  let activeLayer = null;
  let predictiveAbortController = null;
  const WISHLIST_KEY = 'prime-shopping-arena:wishlist:v1';
  const RECENT_KEY = 'prime-shopping-arena:recently-viewed:v1';

  const qs = (selector, context = doc) => context.querySelector(selector);
  const qsa = (selector, context = doc) => Array.from(context.querySelectorAll(selector));

  function announce(message) {
    const region = liveRegion();
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  }

  function showToast(message, type = 'success') {
    let toast = qs('[data-theme-toast]');
    if (!toast) {
      toast = doc.createElement('div');
      toast.setAttribute('data-theme-toast', '');
      toast.setAttribute('role', 'status');
      toast.style.cssText = 'position:fixed;z-index:220;right:18px;bottom:18px;max-width:min(420px,calc(100vw - 36px));padding:13px 16px;border-radius:12px;background:var(--color-primary);color:var(--color-primary-contrast);box-shadow:0 18px 50px rgba(11,22,51,.25);font-weight:750;font-size:.86rem;transform:translateY(20px);opacity:0;transition:transform .2s ease,opacity .2s ease;';
      doc.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.style.background = type === 'error' ? 'var(--color-sale)' : 'var(--color-primary)';
    requestAnimationFrame(() => {
      toast.style.transform = 'translateY(0)';
      toast.style.opacity = '1';
    });
    window.clearTimeout(toast._hideTimer);
    toast._hideTimer = window.setTimeout(() => {
      toast.style.transform = 'translateY(20px)';
      toast.style.opacity = '0';
    }, 3200);
  }

  function setOverlay(visible) {
    const el = overlay();
    if (!el) return;
    if (visible) {
      el.hidden = false;
      requestAnimationFrame(() => el.classList.add('is-visible'));
    } else {
      el.classList.remove('is-visible');
      window.setTimeout(() => {
        if (!activeLayer) el.hidden = true;
      }, 220);
    }
  }

  function getFocusable(container) {
    if (!container) return [];
    return qsa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', container).filter((el) => !el.hidden && el.offsetParent !== null);
  }

  function openLayer(layer, options = {}) {
    if (!layer) return;
    if (activeLayer && activeLayer !== layer) closeLayer(activeLayer, false);
    activeLayer = layer;
    layer.classList.add('is-open');
    layer.setAttribute('aria-hidden', 'false');
    layer._returnFocus = options.returnFocus || doc.activeElement;
    doc.body.classList.add(layer.matches('.search-modal, .quick-view-modal') ? 'modal-open' : 'drawer-open');
    setOverlay(true);

    window.setTimeout(() => {
      const focusTarget = qs('[autofocus], input[type="search"], button, a[href]', layer);
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }, 80);
  }

  function closeLayer(layer, restoreFocus = true) {
    if (!layer) return;
    layer.classList.remove('is-open');
    layer.setAttribute('aria-hidden', 'true');
    if (activeLayer === layer) activeLayer = null;
    doc.body.classList.remove('drawer-open', 'modal-open');
    setOverlay(false);
    if (restoreFocus && layer._returnFocus && typeof layer._returnFocus.focus === 'function') {
      layer._returnFocus.focus({ preventScroll: true });
    }
  }

  function closeAllLayers() {
    qsa('.drawer.is-open, .mobile-menu.is-open, .search-modal.is-open, .facets.is-open, .quick-view-modal.is-open').forEach((layer) => closeLayer(layer, false));
    activeLayer = null;
    doc.body.classList.remove('drawer-open', 'modal-open');
    setOverlay(false);
  }

  function openCart(returnFocus) {
    const drawer = qs('[data-cart-drawer]');
    if (drawer) openLayer(drawer, { returnFocus });
  }

  function formatMoney(cents, format = config.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents || 0);
    const placeholderRegex = /\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator)\s*\}\}/;
    const match = format.match(placeholderRegex);
    if (!match) return (value / 100).toFixed(2);

    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (Number.isNaN(number) || number === null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? parts[0] + decimal + parts[1] : parts[0];
    };

    let replacement;
    switch (match[1]) {
      case 'amount':
        replacement = formatWithDelimiters(value, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        replacement = formatWithDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        replacement = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        replacement = formatWithDelimiters(value, 0, '.', ',');
        break;
      default:
        replacement = formatWithDelimiters(value);
    }
    return format.replace(placeholderRegex, replacement);
  }

  function readStoredList(key) {
    try {
      const value = JSON.parse(window.localStorage.getItem(key) || '[]');
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  }

  function writeStoredList(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      return false;
    }
  }

  function escapeHTML(value = '') {
    const node = doc.createElement('span');
    node.textContent = String(value);
    return node.innerHTML;
  }

  function safeURL(value, sameOrigin = false) {
    if (!value) return '';
    try {
      const parsed = new URL(value, window.location.origin);
      if (!['http:', 'https:'].includes(parsed.protocol)) return '';
      if (sameOrigin && parsed.origin !== window.location.origin) return '';
      return parsed.href;
    } catch (error) {
      return '';
    }
  }

  function getProductData(button) {
    return {
      id: String(button.dataset.productId || ''),
      handle: button.dataset.productHandle || '',
      title: button.dataset.productTitle || 'Saved product',
      url: button.dataset.productUrl || '',
      image: button.dataset.productImage || '',
      price: Number(button.dataset.productPrice || 0),
      variantId: String(button.dataset.variantId || ''),
      simple: button.dataset.productSimple === 'true',
      available: button.dataset.productAvailable === 'true'
    };
  }

  function updateWishlistControls(items = readStoredList(WISHLIST_KEY)) {
    const savedIds = new Set(items.map((item) => String(item.id)));
    qsa('[data-wishlist-count]').forEach((count) => {
      count.textContent = items.length;
      if (count.classList.contains('cart-count-bubble')) count.hidden = items.length === 0;
    });
    qsa('[data-wishlist-toggle]').forEach((button) => {
      const active = savedIds.has(String(button.dataset.productId));
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const title = button.dataset.productTitle || 'product';
      button.setAttribute('aria-label', active ? `Remove ${title} from wishlist` : `Save ${title} to wishlist`);
      const label = qs('[data-wishlist-label]', button);
      if (label) label.textContent = active ? 'Saved to wishlist' : 'Save to wishlist';
    });
  }

  function wishlistItemMarkup(item) {
    const title = escapeHTML(item.title || 'Saved product');
    const productURL = safeURL(item.url, true) || '#';
    const imageURL = safeURL(item.image);
    const image = imageURL
      ? `<img src="${escapeHTML(imageURL)}" alt="${title}" loading="lazy" width="180" height="210">`
      : '<span class="wishlist-line__placeholder" aria-hidden="true">PS</span>';
    const action = item.simple && item.available && item.variantId
      ? `<form method="post" action="${escapeHTML(routes.cartAdd || `${root}cart/add`)}" data-ajax-cart><input type="hidden" name="id" value="${escapeHTML(item.variantId)}"><input type="hidden" name="quantity" value="1"><button class="button button--primary button--small button--full" type="submit">Add to basket</button></form>`
      : `<a class="button button--primary button--small button--full" href="${escapeHTML(productURL)}">${item.available === false ? 'View details' : 'Choose options'}</a>`;

    return `<article class="wishlist-line" data-wishlist-line="${escapeHTML(item.id)}">
      <a class="wishlist-line__image" href="${escapeHTML(productURL)}">${image}</a>
      <div class="wishlist-line__content">
        <div>
          <a class="wishlist-line__title" href="${escapeHTML(productURL)}">${title}</a>
          <p class="wishlist-line__price">${escapeHTML(formatMoney(item.price))}</p>
        </div>
        <div class="wishlist-line__actions">${action}<button class="wishlist-line__remove" type="button" data-wishlist-remove="${escapeHTML(item.id)}" aria-label="Remove ${title} from wishlist">Remove</button></div>
      </div>
    </article>`;
  }

  function renderWishlist() {
    if (config.settings?.wishlist === false) return;
    const items = readStoredList(WISHLIST_KEY).filter((item) => item && item.id);
    updateWishlistControls(items);
    const body = qs('[data-wishlist-items]');
    const footer = qs('[data-wishlist-footer]');
    if (!body) return;

    if (!items.length) {
      body.innerHTML = `<div class="drawer-cart-empty"><div class="drawer-cart-empty__icon wishlist-empty-icon" aria-hidden="true">♡</div><h3>Save the products that stand out</h3><p style="color:var(--color-muted);max-width:310px;">Tap the heart on any product to keep it here on this device.</p><a class="button button--primary" href="${escapeHTML(routes.root || '/')}">Explore products</a></div>`;
      if (footer) footer.hidden = true;
      return;
    }

    body.innerHTML = `<div class="wishlist-items">${items.map(wishlistItemMarkup).join('')}</div>`;
    if (footer) footer.hidden = false;
  }

  function saveWishlist(items) {
    writeStoredList(WISHLIST_KEY, items.slice(0, 50));
    renderWishlist();
  }

  function toggleWishlist(button) {
    const product = getProductData(button);
    if (!product.id) return;
    const items = readStoredList(WISHLIST_KEY);
    const index = items.findIndex((item) => String(item.id) === product.id);
    if (index >= 0) {
      items.splice(index, 1);
      saveWishlist(items);
      showToast(`${product.title} removed from your wishlist.`);
      announce(`${product.title} removed from your wishlist.`);
    } else {
      items.unshift(product);
      saveWishlist(items);
      showToast(`${product.title} saved to your wishlist.`);
      announce(`${product.title} saved to your wishlist.`);
    }
  }

  function removeWishlistItem(id) {
    const items = readStoredList(WISHLIST_KEY);
    const item = items.find((entry) => String(entry.id) === String(id));
    saveWishlist(items.filter((entry) => String(entry.id) !== String(id)));
    if (item) announce(`${item.title} removed from your wishlist.`);
  }

  function recentCardMarkup(item) {
    const title = escapeHTML(item.title || 'Recently viewed product');
    const productURL = safeURL(item.url, true) || '#';
    const imageURL = safeURL(item.image);
    const image = imageURL
      ? `<img class="product-card__image product-card__image--primary" src="${escapeHTML(imageURL)}" alt="${title}" loading="lazy" width="480" height="480">`
      : '<span class="recently-viewed__placeholder" aria-hidden="true">PS</span>';
    return `<article class="product-card card recently-viewed-card" data-product-id="${escapeHTML(item.id)}">
      <div class="product-card__media media media--square"><a class="product-card__image-link" href="${escapeHTML(productURL)}" aria-label="${title}">${image}</a></div>
      <div class="product-card__content"><h3 class="product-card__title"><a href="${escapeHTML(productURL)}">${title}</a></h3><div class="price"><span class="price__current">${escapeHTML(formatMoney(item.price))}</span></div></div>
    </article>`;
  }

  function initRecentlyViewed(context = doc) {
    if (config.settings?.recentlyViewed === false) return;
    const script = qs('[data-current-product-json]');
    let currentProduct = null;
    if (script && script.dataset.stored !== 'true') {
      try {
        currentProduct = JSON.parse(script.textContent);
        script.dataset.stored = 'true';
        if (currentProduct && currentProduct.id) {
          const stored = readStoredList(RECENT_KEY).filter((item) => String(item.id) !== String(currentProduct.id));
          stored.unshift(currentProduct);
          writeStoredList(RECENT_KEY, stored.slice(0, 12));
        }
      } catch (error) {
        currentProduct = null;
      }
    } else if (script) {
      try { currentProduct = JSON.parse(script.textContent); } catch (error) { currentProduct = null; }
    }

    qsa('[data-recently-viewed-section]', context).forEach((section) => {
      const list = qs('[data-recently-viewed-list]', section);
      if (!list) return;
      const currentId = currentProduct ? String(currentProduct.id) : '';
      const limit = Math.max(2, Number(section.dataset.limit || 4));
      const items = readStoredList(RECENT_KEY).filter((item) => item && item.id && String(item.id) !== currentId).slice(0, limit);
      if (!items.length) {
        section.hidden = true;
        return;
      }
      list.innerHTML = items.map(recentCardMarkup).join('');
      section.hidden = false;
      initReveal(section);
    });
  }

  async function fetchJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        ...(options.headers || {})
      },
      ...options
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || config.strings?.cartError || 'Something went wrong.');
    }
    return data;
  }

  async function refreshCart({ openAfter = false, returnFocus = null } = {}) {
    const sectionUrl = `${root}${root.includes('?') ? '&' : '?'}section_id=cart-drawer`;
    const cartUrl = `${routes.cart || `${root}cart`}.js`;
    const [sectionResponse, cart] = await Promise.all([
      fetch(sectionUrl, { credentials: 'same-origin' }),
      fetchJSON(cartUrl)
    ]);

    if (sectionResponse.ok) {
      const html = await sectionResponse.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextDrawer = qs('[data-cart-drawer]', parsed);
      const currentDrawer = qs('[data-cart-drawer]');
      if (nextDrawer && currentDrawer) currentDrawer.replaceWith(nextDrawer);
    }

    qsa('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = cart.item_count === 0;
    });

    doc.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    if (openAfter) openCart(returnFocus);
    return cart;
  }

  function jsonEndpoint(url) {
    if (!url) return '';
    return url.endsWith('.js') ? url : `${url}.js`;
  }

  async function addToCart(form) {
    const submitButton = qs('[type="submit"]', form);
    const originalText = submitButton ? submitButton.innerHTML : '';
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.innerHTML = '<span>Adding...</span>';
    }

    try {
      const formData = new FormData(form);
      const response = await fetch(jsonEndpoint(routes.cartAdd || `${root}cart/add`), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || data.message || config.strings?.cartError || 'Could not add this item.');

      const title = data.product_title || data.title || 'Item';
      await refreshCart({ openAfter: config.settings?.cartDrawer !== false, returnFocus: submitButton });
      announce(`${title} was added to your basket.`);
      if (config.settings?.cartDrawer === false) showToast(`${title} added to cart.`);
    } catch (error) {
      announce(error.message);
      showToast(error.message, 'error');
    } finally {
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
        submitButton.innerHTML = originalText;
      }
    }
  }

  async function changeCartLine(key, quantity, trigger) {
    if (!key) return;
    if (trigger) trigger.disabled = true;
    try {
      await fetchJSON(`${routes.cartChange || `${root}cart/change`}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity) || 0) })
      });
      await refreshCart({ openAfter: true, returnFocus: trigger });
      announce('Basket updated.');
    } catch (error) {
      showToast(error.message, 'error');
      if (trigger) trigger.disabled = false;
    }
  }

  function initPredictiveSearch(context = doc) {
    qsa('[data-predictive-search-form]', context).forEach((form) => {
      if (form.dataset.initialized === 'true') return;
      form.dataset.initialized = 'true';
      const input = qs('[data-predictive-search-input]', form);
      const results = qs('[data-predictive-search-results]', form.parentElement) || qs('[data-predictive-search-results]');
      if (!input || !results) return;
      let timer;

      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        const term = input.value.trim();
        if (term.length < 2) {
          results.innerHTML = '';
          results.hidden = true;
          input.setAttribute('aria-expanded', 'false');
          return;
        }

        timer = window.setTimeout(async () => {
          if (predictiveAbortController) predictiveAbortController.abort();
          predictiveAbortController = new AbortController();
          results.hidden = false;
          results.innerHTML = '<p style="padding:16px 0;color:var(--color-muted);">Searching...</p>';
          input.setAttribute('aria-expanded', 'true');

          try {
            const endpoint = routes.predictiveSearch || `${root}search/suggest`;
            const url = `${endpoint}?q=${encodeURIComponent(term)}&resources[type]=product,collection,page&resources[limit]=6&section_id=predictive-search`;
            const response = await fetch(url, { signal: predictiveAbortController.signal, credentials: 'same-origin' });
            if (!response.ok) throw new Error('Search unavailable.');
            const html = await response.text();
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const content = qs('#predictive-search-results', parsed) || qs('[data-predictive-search-content]', parsed);
            results.innerHTML = content ? content.innerHTML : html;
          } catch (error) {
            if (error.name !== 'AbortError') results.innerHTML = '<p style="padding:16px 0;color:var(--color-muted);">Search is temporarily unavailable.</p>';
          }
        }, 220);
      });
    });
  }

  function setActiveMedia(rootElement, mediaId) {
    if (!mediaId) return;
    qsa('[data-product-media]', rootElement).forEach((media) => {
      const active = String(media.dataset.mediaId) === String(mediaId);
      media.hidden = !active;
      media.classList.toggle('is-active', active);
    });
    qsa('[data-media-target]', rootElement).forEach((thumb) => {
      const active = String(thumb.dataset.mediaTarget) === String(mediaId);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  function initProductRoot(rootElement) {
    if (!rootElement || rootElement.dataset.initialized === 'true') return;
    rootElement.dataset.initialized = 'true';
    const variantsScript = qs('[data-product-variants]', rootElement);
    const form = qs('[data-main-product-form]', rootElement);
    if (!variantsScript || !form) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      return;
    }

    const selects = qsa('[data-option-select]', rootElement);
    const idInput = qs('input[name="id"]', form);
    const addButton = qs('[data-add-to-cart]', form);
    const addButtonText = qs('[data-add-to-cart-text]', form);
    const price = qs('[data-product-price]', rootElement);
    const compare = qs('[data-compare-price]', rootElement);
    const inventory = qs('[data-inventory-text]', rootElement);
    const stickyVariant = qs('[data-sticky-variant]', rootElement);
    const stickyPrice = qs('[data-sticky-price]', rootElement);

    const updateVariant = () => {
      const selectedOptions = selects.map((select) => select.value);
      const variant = variants.find((item) => item.options.every((value, index) => value === selectedOptions[index]));

      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addButtonText) addButtonText.textContent = config.strings?.unavailable || 'Unavailable';
        return;
      }

      if (idInput) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      rootElement.dataset.currentVariant = variant.id;

      if (price) price.textContent = formatMoney(variant.price);
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
      if (compare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compare.textContent = formatMoney(variant.compare_at_price);
          compare.hidden = false;
          compare.closest('.price')?.classList.add('price--sale');
        } else {
          compare.textContent = '';
          compare.hidden = true;
          compare.closest('.price')?.classList.remove('price--sale');
        }
      }

      if (addButton) addButton.disabled = !variant.available;
      if (stickyButton) stickyButton.disabled = !variant.available;
      if (addButtonText) addButtonText.textContent = variant.available ? (config.strings?.addToCart || 'Add to basket') : (config.strings?.soldOut || 'Sold out');
      if (inventory) inventory.textContent = variant.available ? 'In stock and ready to order' : 'Currently unavailable';
      if (stickyVariant) stickyVariant.textContent = variant.title === 'Default Title' ? '' : variant.title;
      if (variant.featured_media && variant.featured_media.id) setActiveMedia(rootElement, variant.featured_media.id);

      if (!rootElement.hasAttribute('data-quick-view-product')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
      doc.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant, root: rootElement } }));
    };

    selects.forEach((select) => select.addEventListener('change', updateVariant));

    qsa('[data-media-target]', rootElement).forEach((button) => {
      button.addEventListener('click', () => setActiveMedia(rootElement, button.dataset.mediaTarget));
    });

    const stickyButton = qs('[data-sticky-add]', rootElement);
    if (stickyButton && addButton) stickyButton.addEventListener('click', () => addButton.click());

    const stickyBar = qs('[data-sticky-atc]', rootElement);
    if (stickyBar && addButton && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        const entry = entries[0];
        stickyBar.classList.toggle('is-visible', !entry.isIntersecting && window.scrollY > 500);
      }, { threshold: 0.1 });
      observer.observe(addButton);
    }
  }

  function initProductRecommendations(context = doc) {
    qsa('[data-product-recommendations]', context).forEach((element) => {
      if (element.dataset.initialized === 'true' || !element.dataset.url) return;
      element.dataset.initialized = 'true';

      const load = async () => {
        try {
          const response = await fetch(element.dataset.url, { credentials: 'same-origin' });
          if (!response.ok) return;
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const replacement = qs('[data-product-recommendations]', parsed);
          if (replacement && replacement.innerHTML.trim()) {
            element.innerHTML = replacement.innerHTML;
            initReveal(element);
          }
        } catch (error) {
          // Recommendations are optional; fail silently.
        }
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting) {
            observer.disconnect();
            load();
          }
        }, { rootMargin: '300px 0px' });
        observer.observe(element);
      } else {
        load();
      }
    });
  }

  function initCountdowns(context = doc) {
    qsa('[data-countdown]', context).forEach((countdown) => {
      if (countdown.dataset.initialized === 'true') return;
      countdown.dataset.initialized = 'true';
      const end = new Date(countdown.dataset.end).getTime();
      if (!Number.isFinite(end)) {
        countdown.hidden = true;
        return;
      }

      const parts = {
        days: qs('[data-days]', countdown),
        hours: qs('[data-hours]', countdown),
        minutes: qs('[data-minutes]', countdown),
        seconds: qs('[data-seconds]', countdown)
      };

      const tick = () => {
        const distance = end - Date.now();
        if (distance <= 0) {
          window.clearInterval(countdown._timer);
          countdown.innerHTML = '<span class="badge badge--accent">Campaign ended</span>';
          return;
        }
        const days = Math.floor(distance / 86400000);
        const hours = Math.floor((distance % 86400000) / 3600000);
        const minutes = Math.floor((distance % 3600000) / 60000);
        const seconds = Math.floor((distance % 60000) / 1000);
        if (parts.days) parts.days.textContent = String(days).padStart(2, '0');
        if (parts.hours) parts.hours.textContent = String(hours).padStart(2, '0');
        if (parts.minutes) parts.minutes.textContent = String(minutes).padStart(2, '0');
        if (parts.seconds) parts.seconds.textContent = String(seconds).padStart(2, '0');
      };
      tick();
      countdown._timer = window.setInterval(tick, 1000);
    });
  }

  function initReveal(context = doc) {
    const elements = qsa('[data-reveal]:not(.is-visible)', context);
    if (!elements.length) return;
    if (!config.settings?.animations || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      elements.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    elements.forEach((el) => observer.observe(el));
  }

  async function openQuickView(button) {
    if (config.settings?.quickView === false || !button) return;
    const modal = qs('[data-quick-view-modal]');
    const content = qs('[data-quick-view-content]', modal);
    const url = safeURL(button.dataset.quickViewUrl, true);
    if (!modal || !content || !url) return;

    content.innerHTML = '<div class="quick-view-loading" role="status"><span></span><p>Preparing the product view...</p></div>';
    openLayer(modal, { returnFocus: button });

    try {
      const response = await fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      if (!response.ok) throw new Error('Quick view is temporarily unavailable.');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const productView = qs('.quick-view-product', parsed);
      if (!productView) throw new Error('Quick view is temporarily unavailable.');
      content.innerHTML = productView.outerHTML;
      initialize(content);
      const heading = qs('h2', content);
      if (heading) heading.setAttribute('tabindex', '-1');
    } catch (error) {
      content.innerHTML = `<div class="quick-view-error"><p>${escapeHTML(error.message)}</p><a class="button button--primary" href="${escapeHTML(url.split('?')[0])}">View product</a></div>`;
    }
  }

  function activatePrimeTab(button) {
    const rootElement = button?.closest('[data-prime-tabs]');
    if (!rootElement) return;
    const id = button.dataset.primeTab;
    qsa('[data-prime-tab]', rootElement).forEach((tab) => {
      const active = tab === button;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });
    qsa('[data-prime-panel]', rootElement).forEach((panel) => {
      const active = panel.dataset.primePanel === id;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
    });
  }

  function initialize(context = doc) {
    initPredictiveSearch(context);
    renderWishlist();
    initRecentlyViewed(context);
    qsa('[data-product-root]', context).forEach(initProductRoot);
    initProductRecommendations(context);
    initCountdowns(context);
    initReveal(context);
    qsa('[data-prime-tabs]', context).forEach((tabs) => {
      qsa('[data-prime-tab]', tabs).forEach((tab, index) => tab.setAttribute('tabindex', index === 0 ? '0' : '-1'));
    });
  }

  doc.addEventListener('click', (event) => {
    const quickViewOpen = event.target.closest('[data-quick-view-open]');
    if (quickViewOpen) {
      event.preventDefault();
      openQuickView(quickViewOpen);
      return;
    }

    const tabButton = event.target.closest('[data-prime-tab]');
    if (tabButton) {
      event.preventDefault();
      activatePrimeTab(tabButton);
      return;
    }

    const wishlistToggle = event.target.closest('[data-wishlist-toggle]');
    if (wishlistToggle) {
      event.preventDefault();
      toggleWishlist(wishlistToggle);
      return;
    }

    const wishlistOpen = event.target.closest('[data-wishlist-open]');
    if (wishlistOpen) {
      event.preventDefault();
      renderWishlist();
      openLayer(qs('[data-wishlist-drawer]'), { returnFocus: wishlistOpen });
      return;
    }

    const wishlistRemove = event.target.closest('[data-wishlist-remove]');
    if (wishlistRemove) {
      event.preventDefault();
      removeWishlistItem(wishlistRemove.dataset.wishlistRemove);
      return;
    }

    const wishlistClear = event.target.closest('[data-wishlist-clear]');
    if (wishlistClear) {
      event.preventDefault();
      saveWishlist([]);
      announce('Wishlist cleared.');
      return;
    }

    const openMobile = event.target.closest('[data-mobile-menu-open]');
    if (openMobile) {
      event.preventDefault();
      openLayer(qs('[data-mobile-menu]'), { returnFocus: openMobile });
      return;
    }

    const openSearch = event.target.closest('[data-search-open]');
    if (openSearch) {
      event.preventDefault();
      openLayer(qs('[data-search-modal]'), { returnFocus: openSearch });
      return;
    }

    const openCartButton = event.target.closest('[data-cart-open]');
    if (openCartButton) {
      event.preventDefault();
      openCart(openCartButton);
      return;
    }

    const closeButton = event.target.closest('[data-layer-close], [data-drawer-close], [data-search-close], [data-mobile-menu-close], [data-filter-close], [data-quick-view-close]');
    if (closeButton) {
      event.preventDefault();
      closeLayer(closeButton.closest('.drawer, .mobile-menu, .search-modal, .facets, .quick-view-modal'));
      return;
    }

    const filterOpen = event.target.closest('[data-filter-open]');
    if (filterOpen) {
      event.preventDefault();
      openLayer(qs('[data-facets]'), { returnFocus: filterOpen });
      return;
    }

    const overlayTarget = event.target.closest('[data-page-overlay]');
    if (overlayTarget) {
      closeAllLayers();
      return;
    }

    const quantityButton = event.target.closest('[data-quantity-button]');
    if (quantityButton) {
      event.preventDefault();
      const wrapper = quantityButton.closest('.quantity');
      const input = qs('input[type="number"]', wrapper);
      if (!input) return;
      const step = Number(input.step || 1);
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const direction = quantityButton.dataset.quantityButton === 'increase' ? 1 : -1;
      input.value = Math.min(max, Math.max(min, Number(input.value || min) + direction * step));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const cartChange = event.target.closest('[data-cart-change]');
    if (cartChange) {
      event.preventDefault();
      changeCartLine(cartChange.dataset.key, cartChange.dataset.quantity, cartChange);
      return;
    }

    const detailsLink = event.target.closest('.desktop-nav details a');
    if (detailsLink) qsa('.desktop-nav details[open]').forEach((details) => details.removeAttribute('open'));
  });

  doc.addEventListener('submit', (event) => {
    const ajaxCartForm = event.target.closest('form[data-ajax-cart]');
    if (ajaxCartForm) {
      const submitter = event.submitter;
      if (submitter && submitter.closest('.shopify-payment-button')) return;
      event.preventDefault();
      addToCart(ajaxCartForm);
    }
  });

  doc.addEventListener('change', (event) => {
    const cartInput = event.target.closest('[data-cart-quantity-input]');
    if (cartInput) {
      changeCartLine(cartInput.dataset.key, cartInput.value, cartInput);
      return;
    }

    const autoSubmit = event.target.closest('[data-auto-submit]');
    if (autoSubmit) {
      const form = autoSubmit.closest('form');
      if (form) form.requestSubmit();
      return;
    }

    const localizationSelect = event.target.closest('[data-localization-select]');
    if (localizationSelect) {
      const form = localizationSelect.closest('form');
      if (form) form.submit();
    }
  });

  doc.addEventListener('keydown', (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      const searchButton = qs('[data-search-open]');
      openLayer(qs('[data-search-modal]'), { returnFocus: searchButton || doc.activeElement });
      return;
    }

    const currentTab = event.target.closest?.('[data-prime-tab]');
    if (currentTab && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
      const rootElement = currentTab.closest('[data-prime-tabs]');
      const tabs = qsa('[data-prime-tab]', rootElement);
      const index = tabs.indexOf(currentTab);
      let nextIndex = index;
      if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      event.preventDefault();
      activatePrimeTab(tabs[nextIndex]);
      tabs[nextIndex].focus();
      return;
    }

    if (event.key === 'Escape' && activeLayer) {
      closeLayer(activeLayer);
      return;
    }

    if (event.key === 'Tab' && activeLayer) {
      const focusable = getFocusable(activeLayer);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && doc.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && doc.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  doc.addEventListener('click', (event) => {
    if (!event.target.closest('.desktop-nav details')) {
      qsa('.desktop-nav details[open]').forEach((details) => details.removeAttribute('open'));
    }
  });

  doc.addEventListener('variant:changed', (event) => {
    const variant = event.detail?.variant;
    const productRoot = event.detail?.root;
    if (!variant || !productRoot) return;
    qsa('[data-wishlist-toggle]', productRoot).forEach((button) => {
      button.dataset.variantId = variant.id;
      button.dataset.productPrice = variant.price;
      button.dataset.productAvailable = variant.available;
    });
    const productButton = qs('[data-wishlist-toggle]', productRoot);
    if (productButton) {
      const items = readStoredList(WISHLIST_KEY);
      const index = items.findIndex((item) => String(item.id) === String(productButton.dataset.productId));
      if (index >= 0) {
        items[index] = { ...items[index], ...getProductData(productButton) };
        saveWishlist(items);
      }
    }
  });

  window.addEventListener('storage', (event) => {
    if (event.key === WISHLIST_KEY) renderWishlist();
    if (event.key === RECENT_KEY) initRecentlyViewed();
  });

  doc.addEventListener('shopify:section:load', (event) => initialize(event.target));
  doc.addEventListener('shopify:section:unload', () => closeAllLayers());

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }
})();
